import axios from 'axios';
import { apiClient, API_URL } from './client';
import { Chat, ChatMember, Message, MessageReaction } from '../types';

export const MY_TELEGRAM_ID = 7702230077;
export const COORDINATOR_ID = 6815240210;
export const TEACHER_LEAD_ID = 650047314;

type ClientProfile = {
    name?: string;
    is_teacher?: boolean;
    lessons_total?: number;
    lessons_scheduled?: number;
    lessons_all_time?: number;
    purchase_status?: string;
    teacher?: {
        name?: string;
    } | null;
};

type ResolvedMemberRole = 'client' | 'teacher' | null;

export type StudentCandidate = {
    id: number;
    fallbackName?: string;
    preferredName?: string;
    isMe?: boolean;
    role?: string;
};

export type EligibleStudent = {
    id: number;
    name: string;
    lessons_total?: number;
    lessons_scheduled?: number;
};

export type MentionCandidate = {
    id: number;
    name: string;
};

export const selectScheduleCandidates = (candidates: StudentCandidate[]): StudentCandidate[] => {
    const teachers = candidates.filter((candidate) => candidate.role === 'teacher');
    if (teachers.length > 0) return teachers;

    const leads = candidates.filter((candidate) => (
        candidate.role === 'teacher_lead' || candidate.id === TEACHER_LEAD_ID
    ));
    if (candidates.some((candidate) => candidate.role === 'teacher_lead')) return leads;

    const legacyClients = candidates.filter((candidate) => candidate.role === 'client');
    const assignedLegacyClients = legacyClients.filter((candidate) => candidate.id !== TEACHER_LEAD_ID);
    return assignedLegacyClients.length > 0 ? assignedLegacyClients : leads;
};

const clientProfileCache = new Map<number, ClientProfile | null>();
const clientProfileInFlight = new Map<number, Promise<ClientProfile | null>>();

/**
 * Сбрасывает кэш профиля клиента для конкретного ID
 */
export const invalidateClientProfile = (telegramId: number): void => {
    clientProfileCache.delete(telegramId);
    clientProfileInFlight.delete(telegramId);
};

/**
 * Сбрасывает весь кэш профилей клиентов
 */
export const clearClientProfileCache = (): void => {
    clientProfileCache.clear();
    clientProfileInFlight.clear();
};

const fetchClientProfile = async (telegramId: number): Promise<ClientProfile | null> => {
    if (!telegramId) return null;

    if (clientProfileCache.has(telegramId)) {
        return clientProfileCache.get(telegramId) ?? null;
    }

    const inFlight = clientProfileInFlight.get(telegramId);
    if (inFlight) return inFlight;

    const request = (async () => {
        try {
            const response = await axios.post(
                'https://api.innoprog.ru/clients/batch',
                { client_ids: [telegramId] },
                {
                    validateStatus: (status) => status >= 200 && status < 300,
                    headers: {
                        Authorization: 'Bearer bot'
                    }
                }
            );

            const clients = Array.isArray(response.data?.clients) ? response.data.clients : [];
            const item = clients[0];
            if (!item || item.error) {
                clientProfileCache.set(telegramId, null);
                return null;
            }

            const data = item as ClientProfile;
            clientProfileCache.set(telegramId, data);
            return data;
        } catch (error) {
            return null;
        } finally {
            clientProfileInFlight.delete(telegramId);
        }
    })();

    clientProfileInFlight.set(telegramId, request);
    return request;
};

const fetchClientProfilesBatch = async (telegramIds: number[]): Promise<Map<number, ClientProfile | null>> => {
    const resolved = new Map<number, ClientProfile | null>();
    const uniqueIds = Array.from(new Set(telegramIds.filter((id) => typeof id === 'number' && id > 0)));

    if (uniqueIds.length === 0) {
        return resolved;
    }

    const idsToFetch: number[] = [];
    const inFlightPromises: Array<Promise<void>> = [];

    uniqueIds.forEach((id) => {
        if (clientProfileCache.has(id)) {
            resolved.set(id, clientProfileCache.get(id) ?? null);
            return;
        }

        const inFlight = clientProfileInFlight.get(id);
        if (inFlight) {
            inFlightPromises.push(
                inFlight
                    .then((profile) => {
                        resolved.set(id, profile ?? null);
                    })
                    .catch(() => {
                        resolved.set(id, null);
                    })
            );
            return;
        }

        idsToFetch.push(id);
    });

    if (inFlightPromises.length > 0) {
        await Promise.all(inFlightPromises);
    }

    if (idsToFetch.length === 0) {
        return resolved;
    }

    const request = (async () => {
        try {
            const response = await axios.post(
                'https://api.innoprog.ru/clients/batch',
                { client_ids: idsToFetch },
                {
                    validateStatus: (status) => status >= 200 && status < 300,
                    headers: {
                        Authorization: 'Bearer bot'
                    }
                }
            );

            const clients = Array.isArray(response.data?.clients) ? response.data.clients : [];
            const byId = new Map<number, ClientProfile | null>();

            clients.forEach((item: any, index: number) => {
                if (!item || typeof item !== 'object') return;
                const rawId = item.id ?? item.telegram_id;
                let id: number | null = null;
                if (typeof rawId === 'number') id = rawId;
                if (typeof rawId === 'string') {
                    const parsed = Number(rawId);
                    id = Number.isFinite(parsed) ? parsed : null;
                }
                if (!id && idsToFetch[index]) {
                    id = idsToFetch[index];
                }
                if (!id) return;
                const profile = item.error ? null : (item as ClientProfile);
                byId.set(id, profile);
            });

            idsToFetch.forEach((id) => {
                const profile = byId.has(id) ? byId.get(id) ?? null : null;
                clientProfileCache.set(id, profile);
                resolved.set(id, profile);
            });
        } catch (error) {
            idsToFetch.forEach((id) => {
                resolved.set(id, null);
            });
        } finally {
            idsToFetch.forEach((id) => {
                clientProfileInFlight.delete(id);
            });
        }

        return resolved;
    })();

    idsToFetch.forEach((id) => {
        const promise = request
            .then((result) => result.get(id) ?? null)
            .catch(() => null);
        clientProfileInFlight.set(id, promise);
    });

    await request;

    return resolved;
};

const computeStatusFromProfile = (
    profile: ClientProfile | null
): { statusColor: 'red' | 'yellow' | 'green' | 'grey'; lessonsTotal?: number; lessonsScheduled?: number } => {
    if (!profile) {
        return { statusColor: 'grey' };
    }

    const total = profile.lessons_total ?? 0;
    const allTime = profile.lessons_all_time ?? 0;
    const purchaseStatus = profile.purchase_status;

    let statusColor: 'red' | 'yellow' | 'green' | 'grey' = 'grey';

    if (total === 1 && allTime === 0) {
        statusColor = 'red';
    } else if (total <= 1 && allTime > 0) {
        statusColor = 'yellow';
    } else if (purchaseStatus === "Подписка активна") {
        statusColor = 'green';
    }

    return {
        statusColor,
        lessonsTotal: profile.lessons_total,
        lessonsScheduled: profile.lessons_scheduled
    };
};

const getMemberId = (member: ChatMember): number | null => {
    const rawId = member.id ?? member.telegram_id ?? member.user_id ?? member.sender_id;
    if (typeof rawId === 'number') return rawId;
    if (typeof rawId === 'string') {
        const parsed = Number(rawId);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const getMemberDisplayName = (member: ChatMember): string | null => {
    const name = (member.innoprog_name || member.full_name || '').trim();
    return name.length > 0 ? name : null;
};

const normalizePurchaseStatus = (value?: string | null): string | null => {
    if (typeof value !== 'string') return null;
    const normalized = value.trim().toLowerCase();
    return normalized.length > 0 ? normalized : null;
};

const resolveRoleFromProfile = (profile: ClientProfile | null): ResolvedMemberRole => {
    if (!profile) return null;
    if (profile.is_teacher === true) return 'teacher';

    const purchaseStatus = normalizePurchaseStatus(profile.purchase_status);
    if (!purchaseStatus || purchaseStatus === 'неактивен') {
        return null;
    }

    return 'client';
};

const hydrateMissingMemberRoles = async (chats: Chat[]): Promise<Chat[]> => {
    const unresolvedIds = new Set<number>();

    chats.forEach((chat) => {
        (chat.members || []).forEach((member) => {
            if (member.role !== null && member.role !== undefined) return;
            const id = getMemberId(member);
            if (id) unresolvedIds.add(id);
        });
    });

    if (unresolvedIds.size === 0) {
        return chats;
    }

    const profilesById = await fetchClientProfilesBatch(Array.from(unresolvedIds));

    return chats.map((chat) => ({
        ...chat,
        members: (chat.members || []).map((member) => {
            if (member.role !== null && member.role !== undefined) {
                return member;
            }

            const id = getMemberId(member);
            if (!id) return member;

            const resolvedRole = resolveRoleFromProfile(profilesById.get(id) ?? null);
            return resolvedRole ? { ...member, role: resolvedRole } : member;
        })
    }));
};

const hydrateMissingMemberRolesWithProfiles = (
    chats: Chat[],
    profilesById: Map<number, ClientProfile | null>
): Chat[] => {
    return chats.map((chat) => ({
        ...chat,
        members: (chat.members || []).map((member) => {
            if (member.role !== null && member.role !== undefined) {
                return member;
            }

            const id = getMemberId(member);
            if (!id) return member;

            const resolvedRole = resolveRoleFromProfile(profilesById.get(id) ?? null);
            return resolvedRole ? { ...member, role: resolvedRole } : member;
        })
    }));
};

const getTeacherNamesFromMembers = (members: ChatMember[] | undefined): string[] => {
    if (!members) return [];
    const names = members
        .filter((member) => member.role === 'teacher')
        .map((member) => getMemberDisplayName(member))
        .filter((name): name is string => Boolean(name));
    return Array.from(new Set(names));
};

const getClientMemberIds = (chat: Chat): number[] => {
    return (chat.members || [])
        .filter((member) => member.role === 'client')
        .map((member) => getMemberId(member))
        .filter((id): id is number => typeof id === 'number');
};

const resolveStudentIdFromMessages = async (chat: Chat): Promise<number | null> => {
    try {
        const messagesResp = await apiClient.get<any[]>(`/chats/${chat.id}/messages`, {
            params: { limit: 50, offset: 0 }
        });

        const messages = Array.isArray(messagesResp.data) ? messagesResp.data : [];
        const senderIds = Array.from(new Set(messages
            .map((msg) => msg.sender_id)
            .filter((id: any) => typeof id === 'number' && id !== MY_TELEGRAM_ID && id !== COORDINATOR_ID)
        ));

        if (senderIds.length === 0) return null;

        const profilesById = await fetchClientProfilesBatch(senderIds);
        const potentialStudents: { id: number; name?: string }[] = [];
        let studentId: number | null = null;

        for (const msg of messages) {
            if (!msg.sender_id || msg.sender_id === MY_TELEGRAM_ID || msg.sender_id === COORDINATOR_ID) continue;
            const profile = profilesById.get(msg.sender_id) ?? null;
            if (!profile || profile.is_teacher) continue;

            if (!studentId) studentId = msg.sender_id;
            potentialStudents.push({ id: msg.sender_id, name: profile.name });

            if (profile.name && (chat.name.includes(profile.name) || profile.name.includes(chat.name))) {
                return msg.sender_id;
            }
        }

        if (!studentId && potentialStudents.length > 0) {
            studentId = potentialStudents[0].id;
        }

        return studentId;
    } catch (e) {
        console.warn(`Failed to identify student from messages in chat ${chat.id}`, e);
        return null;
    }
};

const extractCandidateSenderIds = (messages: any[]): number[] => {
    return Array.from(new Set(messages
        .map((msg) => msg.sender_id)
        .filter((id: any) => typeof id === 'number' && id !== MY_TELEGRAM_ID && id !== COORDINATOR_ID)
    ));
};

const resolveStudentIdFromMessagesWithProfiles = (
    chat: Chat,
    messages: any[],
    profilesById: Map<number, ClientProfile | null>
): number | null => {
    const potentialStudents: { id: number; name?: string }[] = [];
    let studentId: number | null = null;

    for (const msg of messages) {
        if (!msg.sender_id || msg.sender_id === MY_TELEGRAM_ID || msg.sender_id === COORDINATOR_ID) continue;
        const profile = profilesById.get(msg.sender_id) ?? null;
        if (!profile || profile.is_teacher) continue;

        if (!studentId) studentId = msg.sender_id;
        potentialStudents.push({ id: msg.sender_id, name: profile.name });

        if (profile.name && (chat.name.includes(profile.name) || profile.name.includes(chat.name))) {
            return msg.sender_id;
        }
    }

    if (!studentId && potentialStudents.length > 0) {
        studentId = potentialStudents[0].id;
    }

    return studentId;
};

const resolvePrimaryStudentId = async (chat: Chat): Promise<number | null> => {
    const memberClients = getClientMemberIds(chat);
    if (memberClients.length > 0) {
        return memberClients[0];
    }

    return resolveStudentIdFromMessages(chat);
};

export const resolveEligibleStudents = async (candidates: StudentCandidate[]): Promise<EligibleStudent[]> => {
    const uniqueCandidates = new Map<number, StudentCandidate>();
    candidates.forEach((candidate) => {
        if (!uniqueCandidates.has(candidate.id)) {
            uniqueCandidates.set(candidate.id, candidate);
        }
    });

    const candidateList = Array.from(uniqueCandidates.values());
    const filteredCandidates = candidateList.filter((candidate) => !candidate.isMe && candidate.id !== COORDINATOR_ID);
    const idsNeedingProfiles = filteredCandidates
        .filter((candidate) => candidate.role !== 'teacher' && candidate.role !== 'teacher_lead')
        .map((candidate) => candidate.id);
    const profilesById = await fetchClientProfilesBatch(idsNeedingProfiles);

    const eligible: EligibleStudent[] = [];
    filteredCandidates.forEach((candidate) => {
        const profile = profilesById.get(candidate.id) ?? null;
        if (profile?.is_teacher && candidate.role !== 'teacher' && candidate.role !== 'teacher_lead') return;

        const name = (candidate.preferredName && typeof candidate.preferredName === 'string')
            ? candidate.preferredName
            : (profile?.name && typeof profile.name === 'string')
                ? profile.name
                : (candidate.fallbackName || `ID ${candidate.id}`);

        eligible.push({
            id: candidate.id,
            name,
            lessons_total: profile?.lessons_total,
            lessons_scheduled: profile?.lessons_scheduled
        });
    });

    return eligible;
};

export const resolveMentionCandidates = async (candidates: StudentCandidate[]): Promise<MentionCandidate[]> => {
    const uniqueCandidates = new Map<number, StudentCandidate>();
    candidates.forEach((candidate) => {
        if (!uniqueCandidates.has(candidate.id)) {
            uniqueCandidates.set(candidate.id, candidate);
        }
    });

    const candidateList = Array.from(uniqueCandidates.values());
    const filteredCandidates = candidateList.filter((candidate) => !candidate.isMe);
    const profilesById = await fetchClientProfilesBatch(filteredCandidates.map((candidate) => candidate.id));

    const mentionable: MentionCandidate[] = [];
    filteredCandidates.forEach((candidate) => {
        const profile = profilesById.get(candidate.id) ?? null;
        const name = (candidate.preferredName && typeof candidate.preferredName === 'string')
            ? candidate.preferredName
            : (profile?.name && typeof profile.name === 'string')
                ? profile.name
                : (candidate.fallbackName || `ID ${candidate.id}`);

        mentionable.push({ id: candidate.id, name });
    });

    return mentionable;
};

export const parseServerTimestamp = (value: unknown): Date => {
    if (value instanceof Date) return value;
    if (typeof value !== 'string') return new Date(value as any);
    const normalized = value.replace(' ', 'T');
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(normalized);
    return new Date(hasTimezone ? normalized : `${normalized}Z`);
};

const isSelfSender = (senderId?: number, senderName?: string): boolean => {
    if (senderId === MY_TELEGRAM_ID) return true;
    if (!senderName) return false;
    const normalized = senderName.replace(/^@/, '').trim().toLowerCase();
    return normalized === 'bot';
};

const normalizeSenderAlias = (value?: string): string | undefined => {
    if (!value) return undefined;
    const normalized = value.replace(/^@/, '').trim().toLowerCase();
    if (normalized === 'innoprog_tuto_bot' || normalized === 'innoprog_tutor_bot') return '@Куратор';
    return value;
};

const compareMessagesByTelegramId = (a: Message, b: Message): number => {
    const aId = a.telegramMessageId;
    const bId = b.telegramMessageId;
    if (typeof aId === 'number' && typeof bId === 'number') {
        return aId - bId;
    }
    if (typeof aId === 'number') return -1;
    if (typeof bId === 'number') return 1;
    return a.timestamp.getTime() - b.timestamp.getTime();
};

const mapReactions = (raw: any): MessageReaction[] | undefined => {
    if (!raw) return undefined;

    const normalize = (reaction: any): MessageReaction | null => {
        if (!reaction) return null;
        const emoji = reaction.emoji ?? reaction.reaction ?? reaction.code ?? reaction.symbol;
        if (!emoji) return null;
        let count: number | undefined = reaction.count ?? reaction.total ?? reaction.amount;
        if (typeof count !== 'number') {
            const users = reaction.user_ids ?? reaction.users;
            if (Array.isArray(users)) count = users.length;
        }
        const hasCount = typeof count === 'number' && !Number.isNaN(count);
        const reactedByMeRaw = reaction.is_mine ?? reaction.reacted_by_me ?? reaction.is_my ?? reaction.me;
        let reactedByMe = typeof reactedByMeRaw === 'boolean' ? reactedByMeRaw : undefined;
        if (reactedByMe === undefined) {
            const users = reaction.user_ids ?? reaction.users;
            if (Array.isArray(users)) {
                reactedByMe = users.some((user) => {
                    if (typeof user === 'number') return user === MY_TELEGRAM_ID;
                    if (!user || typeof user !== 'object') return false;
                    const id = user.id ?? user.telegram_id ?? user.sender_id;
                    return id === MY_TELEGRAM_ID;
                });
            }
        }
        return {
            emoji: String(emoji),
            count: hasCount ? count! : 1,
            reactedByMe
        };
    };

    if (Array.isArray(raw)) {
        const reactions = raw.map(normalize).filter(Boolean) as MessageReaction[];
        return reactions.length > 0 ? reactions : undefined;
    }

    if (typeof raw === 'object') {
        const reactions = Object.entries(raw).map(([emoji, count]) => ({
            emoji: String(emoji),
            count: typeof count === 'number' ? count : Number(count),
            reactedByMe: false
        })).filter((item) => !Number.isNaN(item.count) && item.count > 0);
        return reactions.length > 0 ? reactions : undefined;
    }

    return undefined;
};

const mapMessage = async (msg: any): Promise<Message> => {
    const attachments = [];
    if (msg.file_id) {
        let type: 'image' | 'file' | 'audio' = 'file';
        if (msg.message_type === 'photo') {
            type = 'image';
        } else if (msg.message_type === 'voice' || msg.message_type === 'audio') {
            type = 'audio';
        } else if (msg.file_name) {
            const lowerName = msg.file_name.toLowerCase();
            if (lowerName.endsWith('.ogg') || lowerName.endsWith('.oga') || lowerName.endsWith('.mp3')) {
                type = 'audio';
            }
        }

        attachments.push({
            type: type,
            name: msg.file_name || (type === 'audio' ? 'Voice Message' : 'file'),
            url: msg.file_url ? `${API_URL}${msg.file_url}` : '#'
        } as any);
    }

    // Check if sender_id matches my ID
    const isMe = isSelfSender(msg.sender_id, msg.sender_name);
    const senderName = isMe
        ? 'me'
        : (normalizeSenderAlias(msg.sender_name) || msg.sender_name || 'Unknown');
    const replyIsMe = isSelfSender(msg.reply_to_message?.sender_id, msg.reply_to_message?.sender_name);
    const replySenderName = msg.reply_to_message
        ? (replyIsMe ? 'me' : (normalizeSenderAlias(msg.reply_to_message.sender_name) || msg.reply_to_message.sender_name || 'Unknown'))
        : undefined;

    const timestamp = parseServerTimestamp(msg.created_at);
    const editedAt = msg.edited_at ? parseServerTimestamp(msg.edited_at) : undefined;

    return {
        id: msg.id,
        telegramMessageId: msg.telegram_message_id,
        text: msg.text || '',
        sender: senderName,
        senderRawName: msg.sender_name,
        senderId: msg.sender_id,
        timestamp,
        editedAt,
        isRead: typeof msg.is_read === 'boolean' ? msg.is_read : true,
        replyToMessageId: msg.reply_to_message_id,
        replyToMessage: msg.reply_to_message ? {
            telegramMessageId: msg.reply_to_message.telegram_message_id,
            sender: replySenderName || msg.reply_to_message.sender_name || 'Unknown',
            senderRawName: msg.reply_to_message.sender_name,
            senderId: msg.reply_to_message.sender_id,
            text: msg.reply_to_message.text || '',
            messageType: msg.reply_to_message.message_type
        } : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        reactions: mapReactions(msg.reactions)
    };
};

const mapChat = async (chat: any): Promise<Chat> => {
    const members: ChatMember[] = Array.isArray(chat.members) ? chat.members : [];
    return {
    id: chat.id,
    telegramChatId: chat.telegram_chat_id,
    name: chat.chat_title || `Chat ${chat.telegram_chat_id}`,
    unreadCount: chat.unread_count || 0,
    participants: [],
    avatar: undefined,
    teacherName: null,
    teacherNames: getTeacherNamesFromMembers(members),
    members,
    with_teacher: chat.with_teacher || false,
    createdAt: chat.added_at ? parseServerTimestamp(chat.added_at) : undefined,
    // Try to use last_message if backend provides it
    lastMessage: chat.last_message ? await mapMessage(chat.last_message) : undefined
    };
};

const getLastMessageChatRefs = (message: any): { chatId: string | null; telegramChatId: number | null } => {
    const rawChatId = message?.chat_id ?? message?.chat_uuid ?? message?.chatId;
    const rawTelegramChatId = message?.telegram_chat_id ?? message?.telegramChatId;

    const chatId = typeof rawChatId === 'string' && rawChatId.trim().length > 0
        ? rawChatId
        : null;
    const telegramChatId = typeof rawTelegramChatId === 'number'
        ? rawTelegramChatId
        : (typeof rawTelegramChatId === 'string' && rawTelegramChatId.trim().length > 0
            ? Number(rawTelegramChatId)
            : null);

    return {
        chatId,
        telegramChatId: Number.isFinite(telegramChatId) ? telegramChatId : null
    };
};

type LastMessageMapping = {
    chatId: string | null;
    telegramChatId: number | null;
    message: Message;
};

const isLastMessageMappingWithChatId = (item: LastMessageMapping | null): item is LastMessageMapping & { chatId: string } => {
    return item !== null && typeof item.chatId === 'string' && item.chatId.length > 0;
};

const isLastMessageMappingWithTelegramChatId = (item: LastMessageMapping | null): item is LastMessageMapping & { telegramChatId: number } => {
    return item !== null && typeof item.telegramChatId === 'number';
};

export const chatService = {
    getChats: async (): Promise<Chat[]> => {
        const response = await apiClient.get<any[]>('/chats');
        const chats = await Promise.all(response.data.map(mapChat));

        const chatsWithoutClientMembers = chats.filter((chat) => getClientMemberIds(chat).length === 0);
        const messagesByChatId = new Map<string, any[]>();
        if (chatsWithoutClientMembers.length > 0) {
            const messageResponses = await Promise.all(chatsWithoutClientMembers.map(async (chat) => {
                try {
                    const messagesResp = await apiClient.get<any[]>(`/chats/${chat.id}/messages`, {
                        params: { limit: 50, offset: 0 }
                    });
                    return {
                        chatId: chat.id,
                        messages: Array.isArray(messagesResp.data) ? messagesResp.data : []
                    };
                } catch (e) {
                    console.warn(`Failed to identify student from messages in chat ${chat.id}`, e);
                    return {
                        chatId: chat.id,
                        messages: []
                    };
                }
            }));

            messageResponses.forEach(({ chatId, messages }) => {
                messagesByChatId.set(chatId, messages);
            });
        }

        const profileIds = new Set<number>();
        chats.forEach((chat) => {
            (chat.members || []).forEach((member) => {
                const id = getMemberId(member);
                if (!id) return;
                if (member.role === null || member.role === undefined || member.role === 'client') {
                    profileIds.add(id);
                }
            });
        });
        messagesByChatId.forEach((messages) => {
            extractCandidateSenderIds(messages).forEach((id) => profileIds.add(id));
        });

        const profilesById = await fetchClientProfilesBatch(Array.from(profileIds));
        const chatsWithResolvedMemberRoles = hydrateMissingMemberRolesWithProfiles(chats, profilesById);

        const chatsMissingLastMessage = chatsWithResolvedMemberRoles.filter((chat) => !chat.lastMessage);
        let lastMessagesByChatId = new Map<string, Message>();
        let lastMessagesByTelegramChatId = new Map<number, Message>();

        if (chatsMissingLastMessage.length > 0) {
            try {
                const lastMessagesResp = await apiClient.get<any[]>('/chats/last-messages');
                const mappedLastMessages = await Promise.all(
                    (Array.isArray(lastMessagesResp.data) ? lastMessagesResp.data : []).map(async (message) => {
                        const refs = getLastMessageChatRefs(message);
                        if (!refs.chatId && refs.telegramChatId === null) return null;
                        return {
                            ...refs,
                            message: await mapMessage(message)
                        };
                    })
                );

                lastMessagesByChatId = new Map(
                    mappedLastMessages
                        .filter(isLastMessageMappingWithChatId)
                        .map((item) => [item.chatId, item.message])
                );

                lastMessagesByTelegramChatId = new Map(
                    mappedLastMessages
                        .filter(isLastMessageMappingWithTelegramChatId)
                        .map((item) => [item.telegramChatId, item.message])
                );
            } catch (e) {
                console.warn('Failed to fetch last messages batch', e);
            }
        }

        const chatsWithLastMessage = chatsWithResolvedMemberRoles.map((chat) => {
            if (chat.lastMessage) return chat;
            const lastMessage = lastMessagesByChatId.get(chat.id)
                ?? (typeof chat.telegramChatId === 'number'
                    ? lastMessagesByTelegramChatId.get(chat.telegramChatId)
                    : undefined);
            return lastMessage ? { ...chat, lastMessage } : chat;
        });

        const chatsWithTeachers = chatsWithLastMessage.map((chat) => {
            const teacherNames = getTeacherNamesFromMembers(chat.members);
            return { ...chat, teacherNames };
        });

        const chatStudentIds = new Map<string, number>();
        const chatsWithStudents = chatsWithTeachers.map((chat) => {
            const memberClients = getClientMemberIds(chat);
            let studentId: number | null = memberClients.length > 0 ? memberClients[0] : null;

            if (!studentId) {
                const messages = messagesByChatId.get(chat.id) || [];
                studentId = resolveStudentIdFromMessagesWithProfiles(chat, messages, profilesById);
            }

            if (studentId) {
                chatStudentIds.set(chat.id, studentId);
            }

            return chat;
        });

        const chatsWithStatus = chatsWithStudents.map((chat) => {
            const studentId = chatStudentIds.get(chat.id);
            const profile = studentId ? profilesById.get(studentId) ?? null : null;
            return { ...chat, ...computeStatusFromProfile(profile) };
        });

        return chatsWithStatus;
    },

    createChat: async (telegram_chat_id: number): Promise<Chat> => {
        const response = await apiClient.post<any>('/chats', { telegram_chat_id });
        return await mapChat(response.data);
    },

    deleteChat: async (chatId: string): Promise<void> => {
        await apiClient.delete(`/chats/${chatId}`);
    },

    getMessages: async (chatId: string): Promise<Message[]> => {
        const response = await apiClient.get<any[]>(`/chats/${chatId}/messages`);
        const messages = await Promise.all(response.data.map(mapMessage));
        return messages.sort(compareMessagesByTelegramId);
    },

    getMessagesPage: async (chatId: string, limit: number, offset: number): Promise<Message[]> => {
        const response = await apiClient.get<any[]>(`/chats/${chatId}/messages`, {
            params: { limit, offset }
        });
        const messages = await Promise.all(response.data.map(mapMessage));
        return messages.sort(compareMessagesByTelegramId);
    },

    sendMessage: async (chatId: string, text: string, replyToMessageId?: number): Promise<Message> => {
        const payload: any = { text };
        if (replyToMessageId) {
            payload.reply_to_message_id = replyToMessageId;
        }
        const response = await apiClient.post<any>(`/chats/${chatId}/messages`, payload);
        return await mapMessage(response.data);
    },

    updateMessage: async (chatId: string, telegramMessageId: number, text: string): Promise<Message> => {
        const response = await apiClient.put<any>(`/chats/${chatId}/messages/${telegramMessageId}`, { text });
        return await mapMessage(response.data);
    },

    deleteMessage: async (chatId: string, telegramMessageId: number): Promise<void> => {
        await apiClient.delete(`/chats/${chatId}/messages/${telegramMessageId}`);
    },

    sendPhoto: async (chatId: string, file: File, caption?: string, replyToMessageId?: number): Promise<Message> => {
        const formData = new FormData();
        formData.append('photo', file);
        if (caption) {
            formData.append('caption', caption);
        }
        if (replyToMessageId) {
            formData.append('reply_to_message_id', replyToMessageId.toString());
        }
        const response = await apiClient.post<any>(`/chats/${chatId}/messages/photo`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return await mapMessage(response.data);
    },

    sendDocument: async (chatId: string, file: File, caption?: string, replyToMessageId?: number): Promise<Message> => {
        const formData = new FormData();
        formData.append('document', file);
        if (caption) {
            formData.append('caption', caption);
        }
        if (replyToMessageId) {
            formData.append('reply_to_message_id', replyToMessageId.toString());
        }
        const response = await apiClient.post<any>(`/chats/${chatId}/messages/document`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return await mapMessage(response.data);
    },

    sendMediaGroup: async (chatId: string, files: File[], caption?: string, replyToMessageId?: number): Promise<Message[]> => {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('files', file);
        });
        if (caption) {
            formData.append('caption', caption);
        }
        if (replyToMessageId) {
            formData.append('reply_to_message_id', replyToMessageId.toString());
        }
        const response = await apiClient.post<any[]>(`/chats/${chatId}/messages/media-group`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return await Promise.all(response.data.map(mapMessage));
    },

    markRead: async (chatId: string, lastReadMessageId: number): Promise<void> => {
        await apiClient.post(`/chats/${chatId}/read`, { last_read_message_id: lastReadMessageId });
    },

    getMessageReactions: async (chatId: string, telegramMessageId: number): Promise<MessageReaction[]> => {
        const response = await apiClient.get<any>(`/chats/${chatId}/messages/${telegramMessageId}/reactions`);
        return mapReactions(response.data) ?? [];
    },

    addReaction: async (chatId: string, telegramMessageId: number, emoji: string): Promise<void> => {
        await apiClient.post(`/chats/${chatId}/messages/${telegramMessageId}/reactions`, { emoji });
    },

    removeReaction: async (chatId: string, telegramMessageId: number, emoji: string): Promise<void> => {
        await apiClient.delete(`/chats/${chatId}/messages/${telegramMessageId}/reactions/${encodeURIComponent(emoji)}`);
    },

    /**
     * Обновляет статус (цветной кружок) для конкретного чата
     * Сбрасывает кэш профиля студента и загружает свежие данные
     */
    refreshChatStatus: async (chat: Chat): Promise<{ statusColor: 'red' | 'yellow' | 'green' | 'grey'; lessonsTotal?: number; lessonsScheduled?: number }> => {
        const studentId = await resolvePrimaryStudentId(chat);

        if (!studentId) {
            return { statusColor: 'grey' };
        }

        // Сбрасываем кэш для этого студента и загружаем свежий профиль
        invalidateClientProfile(studentId);
        const profile = await fetchClientProfile(studentId);

        return computeStatusFromProfile(profile);
    },

    /**
     * Обновляет статусы (цветные кружки) для списка чатов одним запросом /clients/batch
     */
    refreshChatStatusesBatch: async (
        chats: Chat[]
    ): Promise<Map<string, { statusColor: 'red' | 'yellow' | 'green' | 'grey'; lessonsTotal?: number; lessonsScheduled?: number }>> => {
        const updates = new Map<string, { statusColor: 'red' | 'yellow' | 'green' | 'grey'; lessonsTotal?: number; lessonsScheduled?: number }>();
        if (!Array.isArray(chats) || chats.length === 0) return updates;

        const chatStudentIds = new Map<string, number>();
        const studentIds: number[] = [];

        chats.forEach((chat) => {
            const memberClients = getClientMemberIds(chat);

            if (memberClients.length > 0) {
                const studentId = memberClients[0];
                chatStudentIds.set(chat.id, studentId);
                studentIds.push(studentId);
            }
        });

        const chatsWithoutMembers = chats.filter((chat) => !chatStudentIds.has(chat.id));
        const resolvedFallbacks = await Promise.all(chatsWithoutMembers.map(async (chat) => ({
            chatId: chat.id,
            studentId: await resolveStudentIdFromMessages(chat)
        })));

        resolvedFallbacks.forEach(({ chatId, studentId }) => {
            if (studentId) {
                chatStudentIds.set(chatId, studentId);
                studentIds.push(studentId);
            } else {
                updates.set(chatId, { statusColor: 'grey' });
            }
        });

        const uniqueStudentIds = Array.from(new Set(studentIds));
        uniqueStudentIds.forEach((id) => invalidateClientProfile(id));

        const profilesById = await fetchClientProfilesBatch(uniqueStudentIds);

        chatStudentIds.forEach((studentId, chatId) => {
            const profile = profilesById.get(studentId) ?? null;
            updates.set(chatId, computeStatusFromProfile(profile));
        });

        return updates;
    },

    /**
     * Получает количество непрочитанных сообщений для чата
     */
    getUnreadCount: async (chatId: string): Promise<number> => {
        const response = await apiClient.get<{ unread_count: number }>(`/chats/${chatId}/unread-count`);
        return response.data.unread_count;
    }
};
